import { z } from "zod";

import {
  VibeCardBindingStatusReadSchema,
  VibeCardProjectionReadSchema
} from "./card-binding-enums.schemas";
import { VibeEnvVarNameSchema, VibeEnvVarScopeReadSchema } from "./env-vars.schemas";

// ----------------------------------------------------------------------------
// The binding read shape
// ----------------------------------------------------------------------------

/**
 * `nxc_<grantId>` — the value the app reads out of its environment.
 *
 * An ADDRESS, not a bearer. Presenting it proves nothing, because the broker
 * re-authorizes the calling app's own identity on every call; it names WHICH
 * authority to use, never grants it. That is why this field is returned in
 * full to every caller that may read the environment at all, and why the
 * console renders it in the value column where a secret would otherwise be
 * masked.
 */
export const VibeCardHandleSchema = z
  .string()
  .regex(
    /^nxc_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
    "handle must be nxc_ followed by the grant id"
  );

/**
 * One access card imported into one app's environment under one NAME.
 *
 * Carries no policy document and no credential material — a COUNT of permitted
 * actions rather than the actions, and the credential's display name rather
 * than anything that could be used as one. An app author needs to know what
 * authority they are consuming; they do not need, and must not receive, the
 * authority itself.
 */
export const VibeAppCardBindingSchema = z.object({
  id: z.string().uuid(),
  vibeAppId: z.string().uuid(),
  organizationId: z.string(),
  /** The environment variable name the app reads. Same grammar as a literal. */
  name: VibeEnvVarNameSchema,
  scope: VibeEnvVarScopeReadSchema,
  handle: VibeCardHandleSchema,
  projection: VibeCardProjectionReadSchema,
  status: VibeCardBindingStatusReadSchema,
  accessCardId: z.string().uuid(),
  /** The card's name, as its owner wrote it. */
  accessCardName: z.string(),
  /** The credential the card attenuates, so the reader knows whose authority this is. */
  credentialName: z.string(),
  /** How many actions the card permits. A COUNT — never the policy itself. */
  allowedActionCount: z.number().int().nonnegative(),
  /** The owner's daily tolerance and what is left of it. `null` = uncapped. */
  quotaPerDay: z.number().int().nonnegative().nullable(),
  quotaRemaining: z.number().int().nonnegative().nullable(),
  createdByUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type VibeAppCardBindingDto = z.infer<typeof VibeAppCardBindingSchema>;
